import { useMemo } from 'react';

export interface SearchToken {
  type: 'key-value' | 'general';
  key?: string;
  value: string;
  original: string;
}

export function parseSearchQuery(query: string): SearchToken[] {
  if (!query.trim()) return [];

  const tokens: SearchToken[] = [];
  let current = '';
  let inQuotes = false;
  let quoteChar = '';
  let i = 0;

  while (i < query.length) {
    const char = query[i];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      // 따옴표 시작
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      // 따옴표 끝
      inQuotes = false;
      current += char;
      quoteChar = '';
    } else if (!inQuotes && char === ' ') {
      // 공백으로 토큰 분리 (따옴표 외부)
      if (current.trim()) {
        tokens.push(parseToken(current.trim()));
        current = '';
      }
    } else {
      current += char;
    }
    i++;
  }

  // 마지막 토큰 추가
  if (current.trim()) {
    tokens.push(parseToken(current.trim()));
  }

  return tokens;
}

function parseToken(token: string): SearchToken {
  // key:'value' 또는 key:"value" 형태 파싱
  const keyValueMatch = token.match(/^(.+?):\s*(['"])(.*?)\2$/);
  if (keyValueMatch) {
    const [, key, , value] = keyValueMatch;
    return {
      type: 'key-value',
      key: key.trim(),
      value: value,
      original: token
    };
  }

  // key:value 형태 파싱 (따옴표 없음)
  const simpleKeyValueMatch = token.match(/^(.+?):(.+)$/);
  if (simpleKeyValueMatch) {
    const [, key, value] = simpleKeyValueMatch;
    return {
      type: 'key-value',
      key: key.trim(),
      value: value.trim(),
      original: token
    };
  }

  // 일반 검색어
  return {
    type: 'general',
    value: token.replace(/^['"]|['"]$/g, ''), // 따옴표 제거
    original: token
  };
}

export interface AdvancedSearchOptions<T> {
  data: T[];
  searchQuery: string;
  searchFields: {
    [key: string]: (item: T, value: string) => boolean;
  };
  generalSearchFields: ((item: T, value: string) => boolean)[];
}

export function useAdvancedSearch<T>({
  data,
  searchQuery,
  searchFields,
  generalSearchFields
}: AdvancedSearchOptions<T>) {
  return useMemo(() => {
    if (!searchQuery.trim()) return data;

    const tokens = parseSearchQuery(searchQuery);

    return data.filter(item => {
      return tokens.every(token => {
        if (token.type === 'key-value' && token.key) {
          const searchField = searchFields[token.key.toLowerCase()];
          if (searchField) {
            return searchField(item, token.value);
          }
          // 키가 정의되지 않은 경우 일반 검색으로 처리
          return generalSearchFields.some(field => field(item, token.value));
        } else {
          // 일반 검색
          return generalSearchFields.some(field => field(item, token.value));
        }
      });
    });
  }, [data, searchQuery, searchFields, generalSearchFields]);
}
